import { useIntersectionObserver } from '@vueuse/core';
import { readonly, ref, toValue, watchEffect, type MaybeRefOrGetter } from 'vue';

interface ILoadWhenVisibleOptions {
  eager?: MaybeRefOrGetter<boolean>;
  rootMargin?: string;
}

/** Enable loading near the viewport once, then keep it enabled for this component's lifetime. */
export function useLoadWhenVisible(
  target: MaybeRefOrGetter<HTMLElement | SVGElement | null | undefined>,
  options: ILoadWhenVisibleOptions = {},
) {
  const enabled = ref(toValue(options.eager) ?? false);
  const { isSupported, stop } = useIntersectionObserver(
    () => (enabled.value ? null : toValue(target)),
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) enabled.value = true;
    },
    { rootMargin: options.rootMargin ?? '200px' },
  );

  watchEffect(() => {
    if (toValue(options.eager) || !isSupported.value) enabled.value = true;
    if (enabled.value) stop();
  });

  return readonly(enabled);
}
